import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui/src/components/ui/button';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { cn } from '@repo/ui/src/lib/utils';
import type { ClassRosterRow, ClassSlotRow } from '@repo/shared-types';
import { DatePicker } from '../../components/pickers/DatePicker';
import { useUpdateClassMemberSlots } from '../../hooks/useClasses';
import { fmtHouseDate } from '../../lib/appliedDate';
import { todayHouseIso } from '../../lib/houseToday';

function setsEqual(a: Set<string>, b: readonly string[]): boolean {
  if (a.size !== b.length) return false;
  return b.every((id) => a.has(id));
}

/**
 * THI-267 Task 12 — S5 frame 2 content (README decision 8): the tick list +
 * date field + consequence sentence + footer, with NO opinion on what
 * contains it. `MemberSlotsPopover` below wraps this in a desktop `Popover`;
 * the phone roster's action sheet (ClassDetailPage) renders this same
 * component straight inside a `Drawer` — two hosts, one implementation, so
 * the save logic and copy can never drift between them.
 *
 * Each already-joined khung giờ shows its OWN "từ dd/mm" from
 * `member.memberships` (one `{ slotId, joinedAt }` per live membership).
 * `updateClassMemberSlotsSchema` still takes only ONE `joinedAt` for the
 * whole diff, though — a NEWLY ticked slot has no membership yet to read a
 * date from, so every newly-ticked slot in one save shares one "Từ" field,
 * matching the mock's single-new-slot example but simplified for the rarer
 * case of ticking more than one new slot at once.
 */
export function MemberSlotsForm({
  groupId, member, slots, onDone,
}: {
  groupId: string;
  member: ClassRosterRow;
  /** The class's own khung giờ, already sorted (weekday order) by the caller. */
  slots: ClassSlotRow[];
  onDone: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const update = useUpdateClassMemberSlots(groupId);

  const initialIds = member.slotIds;
  const [checked, setChecked] = useState<Set<string>>(() => new Set(initialIds));
  const [joinedAt, setJoinedAt] = useState(todayHouseIso());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const newlyChecked = [...checked].filter((id) => !initialIds.includes(id));
  const changed = !setsEqual(checked, initialIds);
  const canSave = changed && checked.size > 0 && !update.isPending;

  function toggle(slotId: string) {
    setErrorMessage(null);
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId); else next.add(slotId);
      return next;
    });
  }

  function handleSave() {
    update.mutate(
      {
        enrollmentId: member.enrollmentId,
        slotIds: [...checked],
        ...(newlyChecked.length > 0 ? { joinedAt } : {}),
      },
      {
        onSuccess: () => onDone(),
        onError: (err: Error & { code?: string }) => {
          if (err.code === 'START_DATE_IN_RECONCILED_MONTH' || err.code === 'START_DATE_BEFORE_CLASS') setErrorMessage(err.message);
          // Any other error already toasts via useUpdateClassMemberSlots's own onError.
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-[14px] font-semibold text-surface-900">
        {t('classDetail.popover.title', { name: member.studentName })}
      </p>
      <div className="flex flex-col">
        {slots.map((s) => {
          const isChecked = checked.has(s.id);
          const isNew = isChecked && !initialIds.includes(s.id);
          // Each live membership carries its OWN joinedAt (follow-up API
          // round) — an existing row shows that slot's real join date, not
          // the roster row's aggregate (earliest-across-slots) `joinedAt`.
          const ownJoinedAt = member.memberships.find((m) => m.slotId === s.id)?.joinedAt;
          return (
            <label
              key={s.id}
              className="flex min-h-[36px] items-center gap-[9.6px] border-b border-surface-100 py-[6.4px] text-[13.5px] last:border-b-0"
            >
              <Checkbox checked={isChecked} onCheckedChange={() => toggle(s.id)} className="h-4 w-4 shrink-0" />
              <span className="flex-1">
                {t(`days.${s.dayOfWeek}`)} <span className="tabular">{s.startTime}</span>
                {isChecked && !isNew && ownJoinedAt ? (
                  <span className="text-surface-400"> · {t('classDetail.popover.fromDate', { date: fmtHouseDate(ownJoinedAt) })}</span>
                ) : null}
              </span>
              {isNew ? (
                <span className="shrink-0 rounded-full bg-brand-50 px-[6.4px] py-[1.6px] text-[11px] font-semibold text-brand-700">
                  {t('classDetail.popover.newTag')}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>
      {newlyChecked.length > 0 ? (
        <div className="flex flex-col gap-1 pl-[25.6px]">
          <span className="flex items-center gap-[6.4px] text-[12.5px] text-surface-700">
            {t('classDetail.popover.fromLabel')}
            <DatePicker
              value={joinedAt}
              onChange={(v) => { setErrorMessage(null); setJoinedAt(v); }}
              className={cn('h-[32px] px-2 text-[12.5px]', errorMessage && 'border-destructive ring-2 ring-destructive/15')}
            />
          </span>
          {errorMessage ? <span className="text-[12px] text-destructive">{errorMessage}</span> : null}
        </div>
      ) : null}
      <p className="m-0 text-[12px] text-surface-500">{t('classDetail.popover.consequence')}</p>
      <div className="flex justify-end gap-[6.4px] pt-[3.2px]">
        <Button type="button" variant="outline" size="sm" className="min-h-[44px] sm:min-h-0" onClick={onDone}>
          {t('classDetail.popover.cancel')}
        </Button>
        <Button type="button" size="sm" className="min-h-[44px] sm:min-h-0" onClick={handleSave} disabled={!canSave}>
          {t('classDetail.popover.save')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Desktop host for `MemberSlotsForm`: opened either from the row's hover
 * pencil or its ⋮ menu's "Sửa khung giờ" (both set the SAME `open` state;
 * Radix positions `PopoverContent` off `PopoverTrigger`'s DOM node regardless
 * of which control caused `open` to flip, so one shared trigger element
 * anchors both entry points). Nested inside the page, not inside another
 * dialog — §6.12 only applies to an `AlertDialog` nested inside a
 * `Dialog`/`Sheet`, which this is not.
 *
 * Phone uses `MemberSlotsForm` directly inside the roster's action Drawer
 * instead of this wrapper (ClassDetailPage) — a floating Popover anchored to
 * a trigger inside a closing bottom sheet is not a pattern this codebase
 * has, and the row has no persistent pencil to anchor to at 390 anyway.
 */
export function MemberSlotsPopover({
  open, onOpenChange, groupId, member, slots, trigger, align = 'start',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  member: ClassRosterRow;
  slots: ClassSlotRow[];
  /** The pencil button (or an equivalent anchor) — wrapped in `PopoverTrigger asChild`. */
  trigger: ReactNode;
  align?: 'start' | 'end';
}) {
  // Reset-on-open (the popover stays mounted per row between opens): same
  // "adjust state during render" technique as ResponsiveSheet/ClassFormSheet
  // — remounting MemberSlotsForm via `key` re-runs its lazy initializers.
  const [prevOpen, setPrevOpen] = useState(open);
  const [instance, setInstance] = useState(0);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setInstance((n) => n + 1);
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} collisionPadding={12} className="w-[320px] p-[12.8px] sm:w-[340px]">
        <MemberSlotsForm key={instance} groupId={groupId} member={member} slots={slots} onDone={() => onOpenChange(false)} />
      </PopoverContent>
    </Popover>
  );
}
